/**
 * Phương án xử lý của Giám đốc trên báo giá chờ duyệt (anh Tiến 13/10/2026, DEC-300):
 * "chỉ cần nhắn ở nội dung, hệ thống sẽ tự động chạy: giá bán 23.500đ/kg; vật tư khác giảm 0.5%; tăng vận chuyển…
 *  Hệ thống giảm và lưu lại ở Báo giá, đồng thời gởi thông báo đến nhân viên."
 *
 * Hàm thuần, dùng chung API (nguồn sự thật) và giao diện (gợi ý cú pháp):
 *  1. parsePlan: tách nội dung thành từng câu (xuống dòng, chấm phẩy, dấu phẩy/chấm có khoảng trắng phía sau),
 *     mỗi câu → 1 thao tác theo luật. Câu luật không hiểu → `unparsed` (API hỏi AI tiếp nếu có khóa).
 *  2. applyPlan: áp các thao tác lên dòng báo giá theo thứ tự đã ghi, trả giá mới từng dòng + cảnh báo.
 * Quy ước: "khác / còn lại" = các dòng chưa bị câu trước sửa (trừ dòng vận chuyển). Giá/kg không ghi dòng = mọi dòng
 * tính được kg. % không ghi dòng = mọi dòng hàng (trừ vận chuyển). Dòng ống gió không sửa ở đây (giá nằm trong hộp Tính ống gió).
 */
import { foldVi } from './master-data';
import { PRICE_DP, calcLine, roundTo } from './quote-calc';
import { unitPriceFromKg } from './pricing';

export type PlanTarget =
  | { kind: 'all' }
  | { kind: 'kg' }
  | { kind: 'others' }
  | { kind: 'lines'; lines: number[] }
  | { kind: 'match'; text: string };

export type PlanOp =
  /** Đặt giá bán theo kg (đơn giá ĐVT bán tự tính) */
  | { type: 'price_per_kg'; target: PlanTarget; value: number; source: string }
  /** Đặt đơn giá ĐVT bán */
  | { type: 'unit_price'; target: PlanTarget; value: number; source: string }
  /** Tăng (+) / giảm (−) giá bán theo % */
  | { type: 'price_percent'; target: PlanTarget; percent: number; source: string }
  /** Đặt chiết khấu % của dòng */
  | { type: 'discount'; target: PlanTarget; percent: number; source: string }
  /** Dòng vận chuyển: set = đặt tiền, add = cộng/trừ tiền, percent = tăng/giảm % */
  | { type: 'shipping'; mode: 'set' | 'add' | 'percent'; value: number; source: string };

export interface ParsedPlan {
  ops: PlanOp[];
  /** Câu luật chưa hiểu (kèm lý do) */
  unparsed: { text: string; reason: string }[];
}

export const PLAN_TEXT_MAX = 2000;
export const PLAN_MAX_OPS = 30;

const VERB_UP = /\b(tang|them|nang)\b/;
const VERB_DOWN = /\b(giam|bot|ha|tru)\b/;
const SHIP = /\b(van chuyen|cuoc|phi giao|giao hang|ship|vc)\b/;
const OTHERS = /\b(khac|con lai)\b/;
const ALL = /\b(tat ca|toan bo|ca phieu|moi dong|cac dong)\b/;
const GENERIC = new Set(['gia', 'ban', 'gia ban', 'don gia', 'cho', 'la', 'thanh', 'muc', 'hang', 'vat tu', 'dong', 'cac', 'nhung', 'mat hang']);

/** "23.500" → 23500; "2,5tr" → 2.500.000; "500k" → 500.000; "1.250.000đ" → 1.250.000; "23.5" → 23,5 */
export function parseVnMoney(num: string, suffix?: string | null): number | null {
  const s = num.trim();
  if (!/^\d+(?:[.,]\d+)*$/.test(s)) return null;
  const suf = foldVi(suffix ?? '');
  const mult = /^(tr|trieu|m)$/.test(suf) ? 1_000_000 : /^(k|nghin|ngan)$/.test(suf) ? 1_000 : 1;
  let v: number;
  if (mult !== 1) v = Number(s.replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.'));
  else {
    const parts = s.split(/[.,]/);
    const grouped = parts.length > 1 && parts.slice(1).every((p) => p.length === 3);
    v = grouped ? Number(parts.join('')) : Number(s.replace(',', '.'));
  }
  return Number.isFinite(v) ? Math.round(v * mult * 100) / 100 : null;
}

const NUM = String.raw`(\d+(?:[.,]\d+)*)`;
/** Bỏ dấu, chữ thường, giữ dấu chấm/phẩy/gạch chéo/% (foldVi bỏ cả các dấu này) */
const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
const MONEY_RE = new RegExp(String.raw`${NUM}\s*(trieu|tr|k|nghin|ngan|d|vnd|dong)?\b`);
const PCT_RE = new RegExp(String.raw`${NUM}\s*%`);

function parsePercent(s: string): number | null {
  const m = s.match(PCT_RE);
  if (!m) return null;
  const v = Number(m[1]!.replace(',', '.'));
  return Number.isFinite(v) ? v : null;
}

/** "dòng 1, 3", "dòng 2-4", "dòng 1 và 5" → [1, 3] … */
function lineNumbers(f: string): number[] | null {
  const m = f.match(/\bdong\s+((?:\d+\s*(?:-|den|toi|,|va|\s)\s*)*\d+)/);
  if (!m) return null;
  const out = new Set<number>();
  for (const part of m[1]!.split(/,|\bva\b/)) {
    const r = part.trim().match(/^(\d+)\s*(?:-|den|toi)\s*(\d+)$/);
    if (r) for (let i = Number(r[1]); i <= Math.min(Number(r[2]), Number(r[1]) + 500); i++) out.add(i);
    else for (const x of part.trim().split(/\s+/)) if (/^\d+$/.test(x)) out.add(Number(x));
  }
  return out.size ? [...out].sort((a, b) => a - b) : null;
}

/** Đích của câu: số dòng, "khác/còn lại", "tất cả", hoặc chữ đứng trước động từ/giá (tên hàng) */
function targetOf(f: string, head: string, fallback: PlanTarget): PlanTarget {
  const lines = lineNumbers(f);
  if (lines) return { kind: 'lines', lines };
  if (OTHERS.test(f)) return { kind: 'others' };
  if (ALL.test(f)) return { kind: 'all' };
  const words = head
    .replace(/\b(gia ban|don gia|gia|ban|cho|la|thanh|muc|giam|tang|ha|bot|them|nang)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (words && !GENERIC.has(words) && /[a-z]/.test(words)) return { kind: 'match', text: words };
  return fallback;
}

export function splitPlan(text: string): string[] {
  return text
    .slice(0, PLAN_TEXT_MAX)
    .split(/\n|;|\.\s+(?=\D)|,\s+(?=\D)|\s+-\s+(?=\D)/)
    .map((s) => s.replace(/^[\s\-•*+–·]+|[\s.]+$/g, '').trim())
    .filter((s) => s.length > 0);
}

/** Một câu → thao tác; null = luật không hiểu (lý do ở reason) */
export function parseSentence(s: string): { op: PlanOp } | { reason: string } {
  const f = fold(s);
  const up = VERB_UP.test(f);
  const down = VERB_DOWN.test(f);
  const sign = down && !up ? -1 : 1;

  if (SHIP.test(f)) {
    const pct = parsePercent(s);
    if (pct !== null) return { op: { type: 'shipping', mode: 'percent', value: sign * pct, source: s } };
    const m = f.match(MONEY_RE);
    const v = m ? parseVnMoney(m[1]!, m[2]) : null;
    if (v === null || v <= 0) return { reason: 'chưa ghi số tiền vận chuyển (VD "tăng vận chuyển 500.000đ")' };
    if (up || down) return { op: { type: 'shipping', mode: 'add', value: sign * v, source: s } };
    return { op: { type: 'shipping', mode: 'set', value: v, source: s } };
  }

  const kg = f.match(new RegExp(String.raw`${NUM}\s*(k|nghin|ngan)?\s*(?:d|vnd|dong)?\s*/\s*kg\b`));
  if (kg) {
    const v = parseVnMoney(kg[1]!, kg[2]);
    if (v === null || v <= 0) return { reason: 'giá/kg không hợp lệ' };
    return { op: { type: 'price_per_kg', target: targetOf(f, f.slice(0, kg.index), { kind: 'kg' }), value: v, source: s } };
  }

  const ck = f.match(/\b(ck|chiet khau)\b/);
  if (ck) {
    const pct = parsePercent(s);
    if (pct === null || pct < 0 || pct > 100) return { reason: 'chưa ghi % chiết khấu' };
    return { op: { type: 'discount', target: targetOf(f, f.slice(0, ck.index), { kind: 'all' }), percent: pct, source: s } };
  }

  if (up || down) {
    const pct = parsePercent(s);
    if (pct !== null) {
      if (pct <= 0 || pct >= 100) return { reason: '% tăng/giảm phải trong khoảng 0–100' };
      const verb = f.match(sign < 0 ? VERB_DOWN : VERB_UP)!;
      return { op: { type: 'price_percent', target: targetOf(f, f.slice(0, verb.index), { kind: 'all' }), percent: sign * pct, source: s } };
    }
  }

  const gia = f.match(/\b(gia ban|don gia|gia)\b/);
  if (gia) {
    const rest = f.slice(gia.index! + gia[0].length);
    const m = rest.match(MONEY_RE);
    const v = m ? parseVnMoney(m[1]!, m[2]) : null;
    if (v !== null && v > 0) {
      const target = targetOf(f, f.slice(0, gia.index), { kind: 'all' });
      if (target.kind === 'all') return { reason: 'đặt đơn giá phải ghi dòng hoặc tên hàng (VD "dòng 2 giá 150.000đ"), hoặc ghi theo kg (VD "23.500đ/kg")' };
      return { op: { type: 'unit_price', target, value: v, source: s } };
    }
  }
  return { reason: 'chưa hiểu câu này' };
}

export function parsePlan(text: string): ParsedPlan {
  const ops: PlanOp[] = [];
  const unparsed: ParsedPlan['unparsed'] = [];
  for (const s of splitPlan(text)) {
    const r = parseSentence(s);
    if ('op' in r) ops.push(r.op);
    else unparsed.push({ text: s, reason: r.reason });
  }
  return { ops: ops.slice(0, PLAN_MAX_OPS), unparsed };
}

// ---------------------------------------------------------------------------
// Áp phương án lên dòng
// ---------------------------------------------------------------------------
export interface PlanLine {
  lineNo: number;
  productCode: string | null;
  productName: string;
  spec?: string | null;
  unit: string;
  quantity: number;
  unitPrice: number;
  pricePerKg: number | null;
  /** kg / 1 ĐVT chính (null = không tính theo kg được) */
  kgPerBase: number | null;
  uomFactor: number;
  discountPercent: number;
  vatRate: number;
  baseQuantity?: number | null;
  costPrice?: number | null;
  isShipping: boolean;
  isDuct: boolean;
}

export interface PlanLineChange {
  lineNo: number;
  unitPrice: number;
  /** null = bỏ giá/kg (đặt đơn giá trực tiếp) */
  pricePerKg: number | null;
  discountPercent: number;
  /** Câu phương án đã sửa dòng này */
  sources: string[];
}

export interface PlanResult {
  changes: PlanLineChange[];
  warnings: string[];
}

const fmtTarget = (t: PlanTarget) =>
  t.kind === 'lines' ? `dòng ${t.lines.join(', ')}` : t.kind === 'match' ? `"${t.text}"` : t.kind === 'others' ? 'các dòng khác' : t.kind === 'kg' ? 'các dòng tính theo kg' : 'các dòng';

function selectLines(t: PlanTarget, lines: readonly PlanLine[], touched: Set<number>): PlanLine[] {
  const goods = lines.filter((l) => !l.isShipping);
  switch (t.kind) {
    case 'lines':
      return lines.filter((l) => t.lines.includes(l.lineNo));
    case 'others':
      return goods.filter((l) => !touched.has(l.lineNo));
    case 'kg':
      return goods.filter((l) => !!l.kgPerBase);
    case 'match': {
      const words = foldVi(t.text).split(' ').filter(Boolean);
      return goods.filter((l) => {
        const hay = ` ${foldVi(`${l.productCode ?? ''} ${l.productName} ${l.spec ?? ''}`)} `;
        return words.every((w) => hay.includes(` ${w} `) || (w.length >= 3 && hay.includes(w)));
      });
    }
    default:
      return goods;
  }
}

const vndRound = (v: number) => Math.round(v);

export function applyPlan(ops: readonly PlanOp[], lines: readonly PlanLine[]): PlanResult {
  const state = new Map<number, PlanLineChange>(
    lines.map((l) => [l.lineNo, { lineNo: l.lineNo, unitPrice: l.unitPrice, pricePerKg: l.pricePerKg, discountPercent: l.discountPercent, sources: [] }]),
  );
  const touched = new Set<number>();
  const warnings: string[] = [];
  const byNo = new Map(lines.map((l) => [l.lineNo, l]));
  const kgPrice = (l: PlanLine, ppk: number) => unitPriceFromKg(ppk, l.kgPerBase!, { uom: l.unit, factor: l.uomFactor || 1 });

  for (const op of ops) {
    if (op.type === 'shipping') {
      const ship = lines.filter((l) => l.isShipping);
      if (!ship.length) {
        warnings.push(`"${op.source}": báo giá chưa có dòng Vận chuyển, thêm dòng trên báo giá rồi áp lại`);
        continue;
      }
      if (op.mode !== 'percent' && ship.length > 1) warnings.push(`"${op.source}": có ${ship.length} dòng vận chuyển, chỉ sửa dòng ${ship[0]!.lineNo}`);
      for (const l of op.mode === 'percent' ? ship : ship.slice(0, 1)) {
        const s = state.get(l.lineNo)!;
        const qty = l.quantity > 0 ? l.quantity : 1;
        const net = calcLine({ quantity: l.quantity, unitPrice: s.unitPrice, discountPercent: s.discountPercent, vatRate: l.vatRate }).netAmount;
        let unitPrice: number;
        if (op.mode === 'percent') unitPrice = vndRound(s.unitPrice * (1 + op.value / 100));
        else {
          const target = op.mode === 'set' ? op.value : net + op.value;
          unitPrice = roundTo(target / qty / (1 - s.discountPercent / 100 || 1), PRICE_DP);
        }
        if (unitPrice < 0) {
          warnings.push(`"${op.source}": phí vận chuyển sau khi giảm âm, bỏ qua`);
          continue;
        }
        Object.assign(s, { unitPrice, pricePerKg: null });
        s.sources.push(op.source);
        touched.add(l.lineNo);
      }
      continue;
    }

    const sel = selectLines(op.target, lines, touched);
    if (op.target.kind === 'lines') {
      const missing = op.target.lines.filter((n) => !byNo.has(n));
      if (missing.length) warnings.push(`"${op.source}": báo giá không có dòng ${missing.join(', ')}`);
    }
    if (!sel.length) {
      warnings.push(`"${op.source}": không có dòng nào khớp ${fmtTarget(op.target)}`);
      continue;
    }
    for (const l of sel) {
      if (l.isDuct) {
        warnings.push(`"${op.source}": dòng ${l.lineNo} là ống gió, sửa giá trong hộp Tính ống gió`);
        continue;
      }
      const s = state.get(l.lineNo)!;
      if (op.type === 'price_per_kg') {
        if (!l.kgPerBase) {
          warnings.push(`"${op.source}": dòng ${l.lineNo} (${l.productName}) chưa có quy đổi kg, bỏ qua`);
          continue;
        }
        Object.assign(s, { pricePerKg: roundTo(op.value, PRICE_DP), unitPrice: kgPrice(l, op.value) });
      } else if (op.type === 'unit_price') {
        Object.assign(s, { pricePerKg: null, unitPrice: roundTo(op.value, PRICE_DP) });
      } else if (op.type === 'price_percent') {
        if (s.pricePerKg !== null && l.kgPerBase) {
          const ppk = roundTo(s.pricePerKg * (1 + op.percent / 100), PRICE_DP);
          Object.assign(s, { pricePerKg: ppk, unitPrice: kgPrice(l, ppk) });
        } else Object.assign(s, { pricePerKg: null, unitPrice: vndRound(s.unitPrice * (1 + op.percent / 100)) });
      } else if (op.type === 'discount') {
        s.discountPercent = roundTo(op.percent, 2);
      }
      s.sources.push(op.source);
      touched.add(l.lineNo);
    }
  }
  const changes = [...state.values()].filter((c) => {
    const l = byNo.get(c.lineNo)!;
    return c.unitPrice !== l.unitPrice || c.pricePerKg !== l.pricePerKg || c.discountPercent !== l.discountPercent;
  });
  return { changes, warnings };
}

/** Dòng bán dưới giá vốn: tiền sau CK < giá vốn dòng (dòng chưa có giá vốn không tính) */
export function isLossLine(l: { quantity: number | string; unitPrice: number | string; discountPercent?: number | string | null; vatRate: number | string; costPrice?: number | string | null; baseQuantity?: number | string | null }): boolean {
  const a = calcLine(l);
  return a.costAmount !== null && a.netAmount < a.costAmount;
}
